import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import isObject from '@/utils/isObject';
import mergeQueryParams from '@/utils/mergeQueryParams';
import { parseQuery, stringifyQuery } from '@/utils/queryParams';
import { joinUrlPath } from '@/utils/urlPath';

interface GoToUrlOptions {
    query?: Record<string, unknown>;
    base?: string;
}

type GoToUrl = (...parts: (string | GoToUrlOptions)[]) => void;

function useGoToUrl(): GoToUrl {
    const navigate = useNavigate();
    const location = useLocation();

    const queryParams = useMemo(() => parseQuery(location.search), [location.search]);

    return useCallback<GoToUrl>(
        (...parts) => {
            // if past argument is an object treat it as query params
            const last = parts[parts.length - 1];
            // if we do not specify query here, we will clear all query params
            const opts: GoToUrlOptions =
                isObject(last) && typeof last !== 'string' ? last : { query: {} };
            const pathParts = parts.filter((part): part is string => typeof part === 'string');
            const mergedQueryParams = mergeQueryParams(queryParams, opts.query);

            navigate(
                {
                    pathname: joinUrlPath(opts.base ?? location.pathname, ...pathParts),
                    search: stringifyQuery(mergedQueryParams),
                },
                { state: location.state }
            );
        },
        [location.pathname, location.state, queryParams, navigate]
    );
}

export default useGoToUrl;
